import * as React from 'react';
import {
  Box,
  Button,
  Fade,
  IconButton,
  InputBase,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { formatDollarAmount, getInitials } from '../../utils/global/helper';
import { SOFT_PANEL } from '../../constants/ui';

// Anything else is typed into the amount above.
const PERCENTAGES = [0.15, 0.18, 0.2, 0.25];

const roundToCents = (value: number) => Math.round(value * 100) / 100;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Services after discount — what a percentage preset is a percentage of. */
  base: number;
  amount: number;
  /** The percentage it was picked as, so reopening lands on the same tile. */
  percent: number | null;
  /** Everyone the tip is split between, in the order the API splits it. */
  providers: string[];
  onApply: (tip: { amount: number; percent: number | null }) => void;
}

/** The tip has a destination, so the modal opens on who is receiving it. */
function ProviderBadge({ providers }: { providers: string[] }) {
  const [firstName, lastName] = (providers[0] ?? '').split(/\s+/);
  const initials = getInitials({ firstName, lastName }).toUpperCase();
  const solo = providers.length === 1;

  return (
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: '22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        letterSpacing: '0.02em',
        backgroundColor: theme => theme.palette.primary.light,
        color: theme => theme.palette.primary.main,
        boxShadow: `0 20px 40px -18px rgba(255, 48, 8, 0.35), 0 0 0 6px rgba(255, 214, 206, 0.4)`,
        typography: 'h1',
        fontWeight: 700,
      }}>
      {solo && initials ? initials : null}
      {!solo && providers.length > 1 && (
        <GroupsRoundedIcon sx={{ fontSize: 34 }} />
      )}
      {!providers.length && (
        <VolunteerActivismRoundedIcon sx={{ fontSize: 32 }} />
      )}
    </Box>
  );
}

function PercentTile({
  percent,
  value,
  isSelected,
  onSelect,
}: {
  percent: number;
  value: number;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={isSelected}
      onClick={onSelect}
      sx={{
        minWidth: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        padding: '14px 8px',
        borderRadius: '16px',
        border: '1px solid',
        fontFamily: 'inherit',
        transition:
          'transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease',
        borderColor: isSelected
          ? 'rgba(255, 48, 8, 0.55)'
          : SOFT_PANEL.hairline,
        backgroundColor: isSelected ? '#FFFBFA' : '#FFFFFF',
        boxShadow: isSelected
          ? `0 12px 24px -12px rgba(255, 48, 8, 0.22), 0 0 0 4px rgba(255, 214, 206, 0.45)`
          : `0 1px 0 rgba(20, 18, 15, 0.02)`,
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: 'rgba(255, 48, 8, 0.55)',
          backgroundColor: '#FFFBFA',
        },
        '&:active': { transform: 'translateY(0)' },
        '&:focus-visible': {
          outline: 'none',
          borderColor: theme => theme.palette.primary.main,
          boxShadow: `0 0 0 4px rgba(255, 48, 8, 0.18)`,
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
        },
      }}>
      <Typography
        variant="h4"
        lineHeight={1}
        sx={{ color: isSelected ? 'primary.main' : 'text.primary' }}>
        {Math.round(percent * 100)}%
      </Typography>
      <Typography
        variant="caption"
        fontWeight={600}
        lineHeight={1}
        sx={{ color: 'text.tertiary' }}>
        {formatDollarAmount(value)}
      </Typography>
    </Box>
  );
}

export default function TipModal({
  open,
  onClose,
  base,
  amount,
  percent: appliedPercent,
  providers,
  onApply,
}: Props) {
  const [tip, setTip] = React.useState(amount);
  const [percent, setPercent] = React.useState<number | null>(
    appliedPercent ?? null,
  );
  // The raw text while someone is typing, so a half-entered "1." survives.
  const [draft, setDraft] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setTip(amount);
    setPercent(appliedPercent ?? null);
    setDraft(null);
  }, [open, amount, appliedPercent]);

  const display = draft ?? tip.toFixed(2);

  const selectPercent = (value: number) => {
    setPercent(value);
    setTip(roundToCents(base * value));
    setDraft(null);
  };

  const apply = (tipToApply: { amount: number; percent: number | null }) => {
    onApply(tipToApply);
    onClose();
  };

  // Where the money lands, which does not change with the amount.
  let caption = '';
  if (providers.length === 1) {
    caption = `Goes to ${providers[0]}`;
  } else if (providers.length === 2) {
    caption = `Split evenly between ${providers[0]} and ${providers[1]}`;
  } else if (providers.length > 2) {
    caption = `Split evenly between ${providers.length} providers`;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="tip-modal-title"
      disableAutoFocus
      closeAfterTransition>
      <Fade in={open} timeout={220}>
        <Box
          role="dialog"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 520 },
            maxHeight: '92vh',
            overflowY: 'auto',
            borderRadius: '20px',
            boxSizing: 'border-box',
            background: SOFT_PANEL.background,
            boxShadow: SOFT_PANEL.shadow,
            outline: 'none',
          }}>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 2,
              color: 'text.secondary',
              '&:hover': { backgroundColor: SOFT_PANEL.wash },
            }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              px: { xs: 3, sm: 4 },
              pt: 4,
              pb: { xs: 3, sm: 4 },
              boxSizing: 'border-box',
              width: '100%',
            }}>
            <Stack alignItems="center" gap={2.5}>
              <ProviderBadge providers={providers} />

              <Stack alignItems="center" gap={1.5} width="100%">
                <Typography
                  variant="overline"
                  id="tip-modal-title"
                  lineHeight={1}
                  sx={{
                    color: 'text.tertiary',
                  }}>
                  Add tip
                </Typography>

                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(4, 1fr)',
                  }}
                  gap={1.25}
                  width="100%">
                  {PERCENTAGES.map(value => (
                    <PercentTile
                      key={value}
                      percent={value}
                      value={roundToCents(base * value)}
                      isSelected={percent === value}
                      onSelect={() => selectPercent(value)}
                    />
                  ))}
                </Box>

                <InputBase
                  value={display}
                  placeholder="0.00"
                  startAdornment={
                    <Typography
                      variant="h4"
                      component="span"
                      lineHeight={1.1}
                      marginRight={0.5}
                      sx={{ color: 'text.tertiary' }}>
                      $
                    </Typography>
                  }
                  inputProps={{
                    inputMode: 'decimal',
                    'aria-label': 'Tip amount in dollars',
                  }}
                  onFocus={() => setDraft(tip ? tip.toFixed(2) : '')}
                  onBlur={() => setDraft(null)}
                  onChange={event => {
                    setDraft(event.target.value);
                    setTip(roundToCents(Number(event.target.value) || 0));
                    setPercent(null);
                  }}
                  sx={{
                    width: '100%',
                    justifyContent: 'center',
                    padding: '13px 18px',
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: SOFT_PANEL.hairline,
                    backgroundColor: '#FFFFFF',
                    boxShadow: `0 1px 0 rgba(20, 18, 15, 0.02)`,
                    color: 'text.primary',
                    cursor: 'text',
                    transition:
                      'border-color 160ms ease, box-shadow 160ms ease',
                    '&:hover': { borderColor: 'rgba(255, 48, 8, 0.55)' },
                    '&.Mui-focused': {
                      borderColor: theme => theme.palette.primary.main,
                      boxShadow: `0 0 0 4px rgba(255, 48, 8, 0.18)`,
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      transition: 'none',
                    },
                    '& input': {
                      // Sized to its own content so the pair stays centred.
                      width: `${Math.max(4, display.length) + 0.5}ch`,
                      flex: 'none',
                      padding: 0,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.1,
                      typography: 'h1',
                    },
                  }}
                />

                {!!caption && (
                  <Typography
                    variant="subtitle2"
                    textAlign="center"
                    sx={{ color: 'text.secondary' }}>
                    {caption}
                  </Typography>
                )}
              </Stack>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              marginTop={3.5}>
              <Button
                onClick={() => apply({ amount: 0, percent: null })}
                sx={{
                  minWidth: 0,
                  padding: 0,
                  textTransform: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                  },
                  typography: 'h5',
                }}>
                No tip
              </Button>
              <Button
                variant="contained"
                className="primary"
                onClick={() => apply({ amount: tip, percent })}
                sx={{
                  minWidth: 0,
                  padding: '12px 32px',
                  borderRadius: '50px',
                  textTransform: 'none',
                  typography: 'h4',
                  fontWeight: 700,
                }}>
                Update
              </Button>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
